import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Panel } from "@/components/admin/Panel";
import { FarmVideoPanel } from "@/components/admin/FarmVideoPanel";
import { CalculatorSettingsPanel } from "@/components/admin/CalculatorSettingsPanel";
import { ChangePasswordPanel } from "@/components/admin/ChangePasswordPanel";
import { PageTitle } from "@/pages/admin/AdminLayout";
import { useSiteSettings } from "@/context/SettingsContext";
import { api, formatErr } from "@/lib/api";

const TierRow = ({ t, onChange }) => {
  const set = (k, num) => (e) => onChange({ ...t, [k]: num ? Number(e.target.value) : e.target.value });
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 items-center rounded-xl border border-border p-3" data-testid={`settings-tier-${t.key}`}>
      <Input value={t.name} onChange={set("name")} className="h-10 rounded-xl" placeholder="Name" data-testid={`settings-tier-name-${t.key}`} />
      <Input type="number" value={t.price} onChange={set("price", true)} className="h-10 rounded-xl" placeholder="Price" data-testid={`settings-tier-price-${t.key}`} />
      <Input type="number" value={t.intros} onChange={set("intros", true)} className="h-10 rounded-xl" placeholder="Intro limit" data-testid={`settings-tier-intros-${t.key}`} />
      <Input value={t.intros_label} onChange={set("intros_label")} className="h-10 rounded-xl" placeholder="Label" data-testid={`settings-tier-label-${t.key}`} />
      <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={t.best_value} onCheckedChange={(v) => onChange({ ...t, best_value: v })} data-testid={`settings-tier-best-${t.key}`} />Best Value</label>
    </div>
  );
};

export default function AdminSettings() {
  const { reload } = useSiteSettings();
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch((e) => toast.error(formatErr(e)));
  }, []);
  if (!s) return null;

  const save = async () => {
    try {
      const { data } = await api.put("/admin/settings", { admin_email: s.admin_email, notify_email: s.notify_email, savings_pct: Number(s.savings_pct), savings_range: !!s.savings_range, savings_min_pct: Number(s.savings_min_pct), savings_max_pct: Number(s.savings_max_pct), guaranteed_min_pct: Number(s.guaranteed_min_pct), tiers: s.tiers, faqs: s.faqs });
      setS(data);
      reload();
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const setFaq = (i, k) => (e) => setS({ ...s, faqs: s.faqs.map((f, j) => (j === i ? { ...f, [k]: e.target.value } : f)) });

  return (
    <div data-testid="admin-settings-page" className="space-y-6 max-w-5xl">
      <PageTitle title="Settings" sub="Edit tiers, FAQ and the admin email without touching code.">
        <button onClick={save} className="inline-flex h-10 items-center gap-2 px-5 rounded-full bg-primary text-primary-foreground font-bold text-sm" data-testid="settings-save-button"><Save className="h-4 w-4" />Save changes</button>
      </PageTitle>
      <Panel title="Admin email" testId="settings-admin-email-panel">
        <Input value={s.admin_email} onChange={(e) => setS({ ...s, admin_email: e.target.value })} className="h-11 rounded-xl max-w-md" data-testid="settings-admin-email-input" />
        <p className="text-xs text-muted-foreground mt-2">Public contact email shown on the site and used as Reply-To on every email.</p>
      </Panel>
      <Panel title="Notification inbox" testId="settings-notify-email-panel">
        <Input value={s.notify_email || ""} onChange={(e) => setS({ ...s, notify_email: e.target.value })} className="h-11 rounded-xl max-w-md" data-testid="settings-notify-email-input" />
        <p className="text-xs text-muted-foreground mt-2">Receives all admin notifications (new applications, ID uploads, interview requests, contact messages, add-on requests, profile changes) and a copy of every applicant/member email.</p>
      </Panel>
      <CalculatorSettingsPanel s={s} setS={setS} />
      <FarmVideoPanel />
      <Panel title="Membership tiers" testId="settings-tiers-panel">
        <div className="space-y-3">
          {s.tiers.map((t, i) => <TierRow key={t.key} t={t} onChange={(nt) => setS({ ...s, tiers: s.tiers.map((x, j) => (j === i ? nt : x)) })} />)}
        </div>
      </Panel>
      <Panel title="FAQ" testId="settings-faq-panel" action={<button onClick={() => setS({ ...s, faqs: [...s.faqs, { q: "New question", a: "Answer" }] })} className="h-9 px-4 rounded-full border border-border text-sm font-semibold inline-flex items-center gap-1" data-testid="settings-faq-add"><Plus className="h-4 w-4" />Add</button>}>
        <div className="space-y-4">
          {s.faqs.map((f, i) => (
            <div key={i} className="rounded-xl border border-border p-3 space-y-2" data-testid={`settings-faq-${i}`}>
              <div className="flex gap-2">
                <Input value={f.q} onChange={setFaq(i, "q")} className="h-10 rounded-xl font-semibold" data-testid={`settings-faq-q-${i}`} />
                <button onClick={() => setS({ ...s, faqs: s.faqs.filter((_, j) => j !== i) })} className="h-10 w-10 shrink-0 grid place-items-center rounded-full hover:bg-secondary" data-testid={`settings-faq-remove-${i}`} aria-label="Remove"><Trash2 className="h-4 w-4 text-destructive" /></button>
              </div>
              <Textarea value={f.a} onChange={setFaq(i, "a")} rows={2} className="rounded-xl" data-testid={`settings-faq-a-${i}`} />
            </div>
          ))}
        </div>
      </Panel>
      <ChangePasswordPanel />
    </div>
  );
}
